import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { extractErrorMessage } from '../../../utils/errorHandler';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Fab,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PaletteIcon from '@mui/icons-material/Palette';
import { 
  useAuthProfileRetrieveQuery,
  useTeamsTeamMembersSalonMembersRetrieveQuery,
} from '../../../services/GLOWClient';
import { useSalonsMySalonsRetrieveQuery } from '../../../services/salonsApi';
import BasicInfo from './basicInfo';
import AddSalon from './addSalon';
import DeleteSalon from './deleteSalon';
import { theme } from '../../../theme';
import { PrimaryButton } from '../../../SystemDesign/styles';
import { getLocalizedField } from '../../../utils/localizedFields';
import { useSalonAccess } from '../../../context/SalonAccessContext';
import EmployeeUnavailabilityPanel from '../employee/unavailability/EmployeeUnavailabilityPanel';

const Salon = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission, salons: accessSalons } = useSalonAccess();

  const getLocalizedName = (salon) => {
    return getLocalizedField(salon, 'name', i18n.language);
  };

  const [isAddSalonDialogOpen, setIsAddSalonDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [salonToDelete, setSalonToDelete] = useState(null);
  const [absenceSalon, setAbsenceSalon] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch user profile to get owner ID
  const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
  const absenceSalonId = absenceSalon?.salon_id || null;
  const {
    data: absenceMembersData,
    isLoading: absenceMembersLoading,
  } = useTeamsTeamMembersSalonMembersRetrieveQuery(
    absenceSalonId ? { salon: absenceSalonId } : {},
    { skip: !absenceSalonId },
  );
  const absenceMembers = Array.isArray(absenceMembersData)
    ? absenceMembersData
    : (absenceMembersData?.results || []);
  
  // Fetch the authenticated owner/manager salon scope.
  const {
    data: salonRows,
    isLoading: salonsLoading,
    error,
    refetch,
  } = useSalonsMySalonsRetrieveQuery(
    { language: i18n.language },
    { refetchOnMountOrArgChange: true },
  );
  const authorizedSalonIds = new Set(
    accessSalons.map((salon) => Number(salon.salon_id)),
  );
  const salons = (salonRows || []).filter(
    (salon) => authorizedSalonIds.has(Number(salon.salon_id)),
  );

  // Auto-refresh when language changes
  useEffect(() => {
    console.log('[Salon] Language changed to:', i18n.language, ' - refetching salon list');
    refetch();
  }, [i18n.language, refetch]);

  // Force re-render of localized content when language changes
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [i18n.language]);

  const getLocalizedSalonName = useCallback((salon) => {
    return getLocalizedField(salon, 'name', i18n.language);
  }, [i18n.language, refreshKey]);

  // Combined loading state
  const isLoading = profileLoading || salonsLoading;

  // Handle opening the salon creation dialog
  const handleOpenAddDialog = () => {
    setIsAddSalonDialogOpen(true);
  };
  
  // Handle opening the salon page (navigation)
  const handleUpdateClick = (salon) => {
    navigate(`/pro/salon/${salon.salon_id}`);
  };

  // Handle opening the delete confirmation dialog
  const handleDeleteClick = (salon) => {
    setSalonToDelete(salon);
    setDeleteDialogOpen(true);
  };

  const canManageAbsences = (salon) => Boolean(
    salon?.salon_id &&
    hasPermission(salon.salon_id, 'employees.absences.manage')
  );

  const canViewSalon = (salon) => Boolean(
    salon?.salon_id && hasPermission(salon.salon_id, 'salon.view')
  );

  const canManageTheme = (salon) => Boolean(
    salon?.salon_id && hasPermission(salon.salon_id, 'salon.manage_theme')
  );

  const isSalonOwner = (salon) => accessSalons.some(
    (accessSalon) => Number(accessSalon.salon_id) === Number(salon?.salon_id)
      && accessSalon.is_owner,
  );

  const handleOpenTheme = (salon) => {
    navigate(`/pro/salon/${salon.salon_id}/theme`);
  };

  const handleOpenAbsences = (salon) => {
    setAbsenceSalon(salon);
  };

  // Handle salon deletion success
  const handleDeleteSuccess = (deletedSalon, error) => {
    if (deletedSalon) {
      setNotification({
        open: true,
        message: t('pro_dashboard.salon.salon_deleted', { name: getLocalizedName(deletedSalon) }),
        severity: 'success'
      });
      refetch();
    } else if (error) {
      console.error('Failed to delete salon:', error);
      setNotification({
        open: true,
        message: `${t('pro_dashboard.salon.delete_error')}: ${extractErrorMessage(error)}`,
        severity: 'error'
      });
    }
    
    setDeleteDialogOpen(false);
    setSalonToDelete(null);
  };
  

  // Handle closing the notification
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header section */}
      <Box mb={4} sx={{ display: { xs: 'block', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Title */}
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: { xs: 2, md: 0 } }}>
          {t('pro_dashboard.salon.title')}
        </Typography>
        
        {/* Buttons - on same line as title on desktop, separate line on mobile */}
        <Box 
          display="flex" 
          gap={2} 
          alignItems="center"
          sx={{
            justifyContent: 'space-between',
            width: { xs: '100%', md: 'auto' }
          }}
        >
          {/* View mode toggle */}
          {!isLoading && !error && salons && salons.length > 0 && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: `1px solid ${theme.colors.secondary[300]}`,
                  color: theme.colors.text.secondary,
                  '&.Mui-selected': {
                    backgroundColor: theme.colors.secondary[500],
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.colors.secondary[600],
                    }
                  }
                }
              }}
            >
              <ToggleButton value="grid" aria-label={t('pro_dashboard.salon.grid_view')}>
                <GridViewIcon />
              </ToggleButton>
              <ToggleButton value="list" aria-label={t('pro_dashboard.salon.list_view')}>
                <ViewListIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          
          {userProfile?.role === 'professional_responsable' && <Button
            onClick={handleOpenAddDialog}
            variant="contained"
            sx={{
              backgroundColor: theme.colors.secondary[500],
              color: 'white',
              fontSize: '14px',
              padding: { xs: '10px 16px', md: '8px 20px' },
              height: '40px',
              borderRadius: { xs: '8px', md: '20px' },
              textTransform: 'none',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: { xs: '600', md: '500' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&:hover': {
                backgroundColor: theme.colors.secondary[600],
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
            startIcon={<AddIcon sx={{ display: { xs: 'block', md: 'block' } }} />}
          >
            <Box sx={{ 
              display: { xs: 'flex', md: 'block' }, 
              flexDirection: 'column', 
              alignItems: 'flex-start', 
              lineHeight: 1.2 
            }}>
              <span>{t('pro_dashboard.salon.add_salon')}</span>
            </Box>
          </Button>}
        </Box>
      </Box>

      {/* Loading state */}
      {isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {t('pro_dashboard.common.error')}
        </Alert>
      )}

      {/* No salons state */}
      {!isLoading && !error && (!salons || salons.length === 0) && (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', backgroundColor: theme.colors.background.light }}>
          <Typography variant="h6" gutterBottom>
            {t('pro_dashboard.salon.no_salon')}
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {t('pro_dashboard.salon.no_salon_desc')}
          </Typography>
         
        </Paper>
      )}

      {/* Salons display - Grid or List view */}
      {!isLoading && !error && salons && salons.length > 0 && (
        <>
          {viewMode === 'list' ? (
            // Table view
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead sx={{ bgcolor: theme.colors.primary[50] }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.colors.primary[700] }}>
                      {t('pro_dashboard.salon.salon_name')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.colors.primary[700], display: { xs: 'none', md: 'table-cell' } }}>
                      {t('pro_dashboard.salon.address')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.colors.primary[700], display: { xs: 'none', md: 'table-cell' } }}>
                      {t('pro_dashboard.salon.phone')}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: theme.colors.primary[700] }}>
                      {t('pro_dashboard.common.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salons.map((salon) => (
                    <TableRow 
                      key={salon.salon_id}
                      onClick={() => canViewSalon(salon) && handleUpdateClick(salon)}
                      sx={{ 
                        cursor: canViewSalon(salon) ? 'pointer' : 'default',
                        '&:hover': { 
                          bgcolor: theme.colors.secondary[50],
                          transition: 'background-color 0.2s ease',
                          transform: 'scale(1.001)'
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {getLocalizedSalonName(salon)}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {salon.formatted_address || t('pro_dashboard.salon.address_not_specified')}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {salon.phone || t('pro_dashboard.salon.not_specified')}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {canManageAbsences(salon) && (
                            <Tooltip title={`Gérer les congés et absences - ${getLocalizedSalonName(salon)}`}>
                              <IconButton
                                size="small"
                                aria-label={`Gérer les congés et absences - ${getLocalizedSalonName(salon)}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenAbsences(salon);
                                }}
                                sx={{
                                  bgcolor: theme.colors.warning?.[600] || '#c77732',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: theme.colors.warning?.[700] || '#a15c21',
                                    transform: 'translateY(-2px)',
                                    boxShadow: 2,
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <EventBusyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canManageTheme(salon) && (
                            <Tooltip title={t('salon_theme.actions.open')}>
                              <IconButton
                                size="small"
                                aria-label={`${t('salon_theme.actions.open')} - ${getLocalizedSalonName(salon)}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenTheme(salon);
                                }}
                                sx={{
                                  bgcolor: theme.colors.secondary[600],
                                  color: 'white',
                                  '&:hover': { bgcolor: theme.colors.secondary[700] },
                                }}
                              >
                                <PaletteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canViewSalon(salon) && <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateClick(salon);
                            }}
                            sx={{
                              bgcolor: theme.colors.primary[500],
                              color: 'white',
                              '&:hover': {
                                bgcolor: theme.colors.primary[600],
                                transform: 'translateY(-2px)',
                                boxShadow: 2
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>}
                          {isSalonOwner(salon) && <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(salon);
                            }}
                            sx={{
                              bgcolor: theme.colors.error[500],
                              color: 'white',
                              '&:hover': {
                                bgcolor: theme.colors.error[600],
                                transform: 'translateY(-2px)',
                                boxShadow: 2
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            // Grid view
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { 
                  xs: '1fr', 
                  sm: '1fr', 
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)' 
                },
                gap: 3,
              }}
            >
              {salons.map((salon) => (
                <Box key={salon.salon_id} sx={{ width: '100%' }}>
                  <BasicInfo
                    salon={salon}
                    onDelete={isSalonOwner(salon) ? () => handleDeleteClick(salon) : undefined}
                    onUpdate={canViewSalon(salon) ? (salonData) => handleUpdateClick(salonData) : undefined}
                    onManageTheme={canManageTheme(salon) ? handleOpenTheme : undefined}
                    onManageUnavailability={
                      canManageAbsences(salon) ? handleOpenAbsences : undefined
                    }
                  />
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      {/* Floating action button for mobile */}
      {/* <Box sx={{ display: { sm: 'none' }, position: 'fixed', bottom: 16, right: 16 }}>
        <Fab 
          sx={{ bgcolor: theme.colors.secondary[500] }} 
          aria-label="add" 
          onClick={handleOpenAddDialog}
        >
          <AddIcon />
        </Fab>
      </Box> */}

      {/* Add Salon Dialog */}
      <AddSalon 
        open={isAddSalonDialogOpen} 
        onClose={() => {
          setIsAddSalonDialogOpen(false);
        }} 
        onSuccess={(salon, error) => {
          // Handle new salon creation success
          if (salon) {
            console.log('Salon reçu dans onSuccess:', salon);
            
            // Get salon ID - the API returns salon_id
            const salonId = salon.salon_id || salon.id || salon.pk;
            console.log('ID extrait:', salonId);
            
            if (!salonId) {
              console.error('ID du salon introuvable dans:', salon);
              setNotification({
                open: true,
                message: t('pro_dashboard.common.error'),
                severity: 'error'
              });
              setIsAddSalonDialogOpen(false);
              return;
            }
            
            // Close the dialog
            setIsAddSalonDialogOpen(false);
            // Refresh the salons list
            refetch();
            // Navigate to salon details page
            navigate(`/pro/salon/${salonId}`);
            // Show success notification
            setNotification({
              open: true,
              message: t('pro_dashboard.common.success'),
              severity: 'success'
            });
          } else if (error) {
            console.error('Failed to create salon:', error);
            setNotification({
              open: true,
              message: `${t('pro_dashboard.common.error')}: ${extractErrorMessage(error)}`,
              severity: 'error'
            });
            setIsAddSalonDialogOpen(false);
          }
        }}
        isUpdateMode={false}
        salonToUpdate={null}
      />
      
      

      {/* Delete Confirmation Dialog */}
      {salonToDelete && (
        <DeleteSalon
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          salon={salonToDelete}
          onSuccess={handleDeleteSuccess}
        />
      )}

      <Dialog
        open={Boolean(absenceSalon)}
        onClose={() => setAbsenceSalon(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          'aria-label': `Congés et absences - ${
            absenceSalon ? getLocalizedSalonName(absenceSalon) : ''
          }`,
        }}
      >
        <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
          {absenceMembersLoading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          ) : absenceSalonId ? (
            <EmployeeUnavailabilityPanel
              key={absenceSalonId}
              salonId={absenceSalonId}
              members={absenceMembers}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAbsenceSalon(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Salon;
